/**
 * TypeScript types for the SBA Document Viewer feature.
 *
 * These types mirror the JSON shapes returned by the backend
 * viewer API endpoints in sba_viewer_routes.py.
 */

// ─────────────────────────────────────────────────────────────────────
// Table of Contents
// ─────────────────────────────────────────────────────────────────────

export interface TOCEntry {
  id: number;
  // Null for sections without a numeric heading (e.g. title-only
  // summary sections in EMA EPAR Summary-of-Opinion documents).
  section_number: string | null;
  // Shared regulatory-schema column, present on every family's section
  // model except JP_XML (backend guards on hasattr; optional here for
  // that one family). For SBA this is the FDA-printed memo number
  // (e.g. "4.1"), distinct from the internal composite
  // ``section_number`` (e.g. "SEG2.4.1") — null for a ``SEG<N>``
  // container. For most other families it currently equals
  // ``section_number`` (e.g. EU sets them equal at ingest).
  section_number_local?: string | null;
  title: string;
  // Original Japanese heading (jp_pdf bilingual docs only; null elsewhere).
  // The JP viewer matches a hovered Japanese heading block to its section
  // via this so the hover panel shows the canonical TOC ``title`` verbatim.
  title_ja?: string | null;
  // Original heading for zh_pdf / ch_pdf bilingual docs (null elsewhere).
  title_zh?: string | null;
  title_src?: string | null;
  level: number;
  // Raw parent section_number — ambiguous when a document has
  // multiple rows sharing a section_number (e.g. two ``4.3`` rows
  // in the Wegovy Clinical Pharmacology Review).  Prefer
  // ``parent_section_id`` for hierarchy/collapse logic.
  parent_section_number: string | null;
  // Backend-resolved parent row ID.  Unambiguous — each child
  // points at exactly one parent row, even when multiple rows
  // share the same section_number.  Null for top-level rows and
  // for rows whose parent_section_number doesn't match any row.
  parent_section_id: number | null;
  page_start: number | null;
  page_end: number | null;
  section_type: string | null;
  // Presentation grouping (EU SmPC bundles only — absent/null for
  // every other family).  Rows sharing a ``group_key`` belong to the
  // same marketed presentation/strength; ``group_label`` is the banner
  // spec text ("GONAL-f 1050 IU/1.75 mL …"); ``is_group_header`` marks
  // the anchor row, which the sidebar renders as the banner instead of
  // a duplicate list entry.
  group_key?: string | null;
  group_label?: string | null;
  is_group_header?: boolean;
}

// ─────────────────────────────────────────────────────────────────────
// Page listing
// ─────────────────────────────────────────────────────────────────────

export interface PageInfo {
  page_num: number;
  image_url: string | null;
  // Pixel dimensions of the rasterised image — a function of
  // render DPI (150 at ingestion time).  Used by the viewer for
  // aspect-ratio sizing.
  width: number | null;
  height: number | null;
  // Source PDF page dimensions in points (standard letter is
  // 612×792).  Used to normalise chunk bboxes (also stored in
  // points) against the source PDF geometry rather than the
  // render-DPI-dependent pixel size.  Nullable for rows
  // predating the backfill migration (sv2604a20pdf).
  pdf_width_pt?: number | null;
  pdf_height_pt?: number | null;
  is_ocr: boolean;
  // JP PDF only: tables detected on this page (find_tables). Each is a
  // hover region — bbox normalized 0-1, html_ja = the Japanese table
  // grid (translated lazily on hover). Absent for other families.
  table_regions?: TableRegion[] | null;
  // JP leaflet only: logical line regions (header = own box, wrapped bullet =
  // one box). The viewer hovers these instead of raw PyMuPDF blocks. Null →
  // fall back to block grouping.
  line_regions?: LineRegion[] | null;
}

export interface TableRegion {
  bbox: [number, number, number, number]; // normalized 0-1
  rows: number;
  cols: number;
  html_ja: string;
}

export interface LineRegion {
  bbox: [number, number, number, number]; // normalized 0-1
  /** Source text: JP ``text_ja``, zh_pdf ``text_zh``, ch_pdf ``text_src``. */
  text_ja?: string;
  text_zh?: string;
  text_src?: string;
  region_type: 'heading' | 'subheading' | 'bullet' | 'body';
}

/** The source-language text of a hover region, whichever family wrote it. */
export function regionSourceText(r: LineRegion): string {
  return r.text_ja ?? r.text_zh ?? r.text_src ?? '';
}

// ─────────────────────────────────────────────────────────────────────
// Text spans (per-page invisible overlay)
// ─────────────────────────────────────────────────────────────────────

export interface TextSpan {
  text: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  fontSize?: number; // normalized (0-1) relative to page height
  // PyMuPDF per-page block index. Spans sharing a block id form one
  // paragraph-level region (the hover-translate unit on native pages).
  // -1 / undefined = unknown (OCR-region span, or pre-block-index ingest).
  block?: number;
}

// ─────────────────────────────────────────────────────────────────────
// Chunk bounding box and metadata
// ─────────────────────────────────────────────────────────────────────

export interface ChunkBbox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface ChunkMetadata {
  chunk_id: number;
  document_id: number;
  section_id: number;
  section_number: string | null;
  section_title: string | null;
  section_type: string | null;
  chunk_index: number;
  start_char: number;
  end_char: number;
  page_num: number | null;
  bbox: ChunkBbox | null;
  token_count: number | null;
}

// ─────────────────────────────────────────────────────────────────────
// Full-text search within a document
// ─────────────────────────────────────────────────────────────────────

export interface SearchResult {
  page_num: number;
  rank: number;
  headline: string;
}

// ─────────────────────────────────────────────────────────────────────
// API response wrappers
// ─────────────────────────────────────────────────────────────────────

export interface TOCResponse {
  document_id: number;
  sections: TOCEntry[];
}

/** One selected section's text from the parsed chunks (GET .../section-text).
 *  ``html`` is the sanitized formatted body (null → use ``text``); ``leak`` is
 *  set when another section's heading was found inside the text. */
export interface SectionTextResponse {
  document_id: number;
  has_chunks: boolean;
  sections: {
    id: number;
    text: string;
    html: string | null;
    leak: { number: string; offset: number } | null;
    /** Set when text was cut before a swallowed later heading (leak is then null). */
    truncated_at?: string;
    /** 'page_layout' = verified page-layout HTML; 'chunks' = parsed chunks (v3). */
    source?: 'page_layout' | 'chunks';
    /** Layout HTML from a page that failed verification (needs_review). */
    unverified?: boolean;
  }[];
}

/** `filled`: a fresh layout copy is in content_html; `pending`: generation still
 *  running for the document; `settled_unfilled`: finished, section not complete (v3). */
export type SectionLayoutStatus = 'filled' | 'pending' | 'settled_unfilled';

export interface SectionLayoutsEnsureResponse {
  document_id: number;
  sections: { id: number; status: SectionLayoutStatus }[];
}

export interface PagesResponse {
  document_id: number;
  pages: PageInfo[];
  display_mode?: 'normal' | 'source_only';
  display_notice?: string | null;
}

export interface TextSpansResponse {
  document_id: number;
  page_num: number;
  is_ocr: boolean;
  text_spans: TextSpan[];
}

export interface BatchTextSpansResponse {
  document_id: number;
  from: number;
  to: number;
  pages: Record<string, { is_ocr: boolean; text_spans: TextSpan[] }>;
}

// Lazy-loaded line/table regions for a page range (split out of the /pages
// payload so large JP docs don't ship all regions eagerly). Mirrors
// BatchTextSpansResponse. Field types match PageInfo.line_regions /
// PageInfo.table_regions.
export interface RegionsResponse {
  document_id: number;
  from: number;
  to: number;
  pages: Record<string, { line_regions: LineRegion[] | null; table_regions: TableRegion[] | null }>;
}

export interface ChunkResponse extends ChunkMetadata {}

export interface ChunkBatchResponse {
  document_id: number;
  chunks: ChunkMetadata[];
}

export interface SearchResponse {
  document_id: number;
  query: string;
  total_results: number;
  results: SearchResult[];
}
